// MainLayout.js
import React, { useState, useEffect } from "react";
import { Layout, Menu, Image, Switch, Dropdown, message, Flex, Badge, Button } from "antd";
import {
  UserOutlined,
  BulbOutlined,
  LogoutOutlined,
  BellOutlined,
  ColumnWidthOutlined,
  KeyOutlined
} from "@ant-design/icons";
import { ReactComponent as BadgeIcon } from "../assets/nav_icons/badge-light.svg";
import { ReactComponent as UserIcon } from "../assets/nav_icons/user-light.svg";
import { ReactComponent as MessageIcon } from "../assets/nav_icons/message-middle-light.svg";
import { ReactComponent as LayerIcon } from "../assets/nav_icons/layer-group-light.svg";
import { ReactComponent as ClipboardIcon } from "../assets/nav_icons/clipboard-list-check-light.svg";
import { ReactComponent as SquarePlusIcon } from "../assets/nav_icons/square-plus-light.svg";
import { Routes, Route, useNavigate } from "react-router-dom";
import Dashboard from "../pages/dashboard";
import BadgeEditor from "../pages/badgeEditor";
import CategoryManagement from "../pages/categoryManagement";
import Logo from "../assets/img/logo.svg";
import UserManagement from "../pages/userManagement";
import LandingEditor from "../pages/landingEditor";
import LandingEditorDesktop from "../pages/landingEditorDesktop";
import BadgePreview from "../pages/badgePreview";
import BadgeEdit from "../pages/badgeEdit";
import ChecklistPage from "../pages/checklistPage";
import InfoMessage from "../pages/infoMessage";
import NotificationsPage from "../pages/notifications";
import FanTemplate from "../pages/fanTemplate";
import KeyManagement from "../pages/KeyManagement";
import { getParamsApi } from "../services/api";
import { Client } from '@stomp/stompjs';
import { useSelector } from 'react-redux';

const MainLayout = () => {
  const [darkMode, setDarkMode] = useState(false);
  const { Header, Content, Footer, Sider } = Layout;
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const [notificationCount, setNotificationCount] = useState(0);

  const { groups: userRole } = useSelector((state) => state.auth);
  const currentRole = Array.isArray(userRole) ? userRole[0] : userRole;

  useEffect(() => {
    const loadNotifications = async () => {
      try {
        const userEmail = localStorage.getItem("userEmail")?.toLowerCase();
        console.log("fetching notifications", userEmail);
        if (!userEmail) {
          console.log("no user email found");
          return;
        }

        const response = await getParamsApi("/notification", { owner: userEmail });
        console.log("notification response:", response);

        const unreadCount = response.filter(n => !n.read).length;
        setNotificationCount(unreadCount);
        localStorage.setItem('notificationCount', unreadCount);

      } catch (error) {
        console.error('Error fetching notifications:', error);
        message.error('Failed to fetch notifications');
      }
    };

    loadNotifications();

    const userEmail = localStorage.getItem("userEmail")?.toLowerCase();
    if (!userEmail) return;

    const stompClient = new Client({
      brokerURL: 'ws://ec2-54-221-60-147.compute-1.amazonaws.com:8080/ws',
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000
    });

    stompClient.onConnect = (frame) => {
      console.log('Connected to WebSocket');
      
      stompClient.subscribe(`/approval/${userEmail}`, (message) => {
        try {
          const notifications = JSON.parse(message.body).content;
          console.log('New notification received:', notifications);

          setNotificationCount(prevCount => prevCount + 1);

          message.info({
            content: notifications,
            duration: 4,
            onClick: () => navigate('/notifications')
          });
        } catch (error) {
          console.error('Error processing notification:', error);
        }
      });
    };

    stompClient.onWebSocketError = (error) => {
      console.error('WebSocket Error:', error);
      message.error('Failed to connect to notification service');
    };

    stompClient.onStompError = (frame) => {
      console.error('STOMP Error:', frame);
      message.error('Connection error with notification service');
    };

    stompClient.activate();

    return () => {
      if (stompClient.active) {
        stompClient.deactivate();
      }
    };
  }, []);

  const handleDarkModeChange = (checked) => {
    setDarkMode(checked);
  };
  function getItem(label, key, icon, children, type) {
    return {
      key,
      icon,
      children,
      label,
      type,
    };
  }
  const userGroups = JSON.parse(localStorage.getItem("userGroups") || "[]");
  const isAdmin = userGroups.includes("admin");

  const menuItems = [
    getItem("Badges", "dashboard", <BadgeIcon style={{ width: '16px', height: '16px', fill: 'currentColor' }} />),
    
    ...(currentRole !== 'viewer' ? [
      getItem(
        collapsed ? <div style={{ borderTop: '1px solid #D9D9D9', padding: '0px 0' }} /> : <span style={{ color: '#9A9A9A' }}>Create</span>,
        "grp",
        null,
        [
          getItem("Badge", "badge-editor", <SquarePlusIcon style={{ width: '16px', height: '16px', fill: 'currentColor' }} />),
          getItem("Fan Templates", "fan-template", <ColumnWidthOutlined style={{ width: '16px', height: '16px', fill: 'currentColor' }} />),
        ],
        "group"
      ),
    ] : []),

    getItem(
      collapsed ? <div style={{ borderTop: '1px solid #D9D9D9', padding: '4px 0' }} /> : <span style={{ color: '#9A9A9A' }}>Management</span>,
      "grp",
      null,
      [
        getItem("Categories", "category-management", <LayerIcon style={{ width: '16px', height: '16px', fill: 'currentColor' }} />),
        getItem("Checklists", "checklist-management", <ClipboardIcon style={{ width: '16px', height: '16px', fill: 'currentColor' }} />),
        getItem("Info Message", "info-message-management", <MessageIcon style={{ width: '16px', height: '16px', fill: 'currentColor' }} />),
        isAdmin ? getItem("Secret Key", "key-management", <KeyOutlined style={{ width: '16px', height: '16px' }} />) : null,
        ...(isAdmin
          ? [getItem("Users", "user-management", <UserIcon style={{ width: '16px', height: '16px', fill: 'currentColor' }} />)]
          : []),
      ],
      "group"
    ),
  ];
  const items = [
    /*     {
      label: "Profile",
      key: "profile",
      icon: <UserOutlined />,
    }, */
    {
      label: "Logout",
      key: "logout",
      icon: <LogoutOutlined />,
      danger: true,
    },
  ];
  const handleMenuClick = (e) => {
    if (e.key === "logout") {
      localStorage.clear();
      navigate("/login");
    }
  };
  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  const updateNotificationCount = (count) => {
    setNotificationCount(count);
  };

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
        style={{ background: '#fff' }}
        theme="light"
        className="site-layout-background"
      >
        <div className="demo-logo-vertical mb-5">
          <Image 
            src={collapsed ? Logo : Logo} 
            width={collapsed ? "35" : "100"} 
            preview={false} 
            style={{ 
              padding: collapsed ? '8px' : '0',
              marginTop: '10px'
            }}
          />
        </div>
        <Menu
          theme="light"
          onClick={({ key }) => {
            navigate(key);
          }}
          mode="inline"
          items={menuItems}
        />
      </Sider>
      <Layout>
        <Header
          style={{
            background: darkMode ? "#001529" : "#fff",
            margin: "10px 16px",
            borderRadius: "10px",
          }}
        >
          <Flex align="center" justify="flex-end" gap="small">
            <div>
              <Switch
                checked={darkMode}
                onChange={handleDarkModeChange}
                checkedChildren={<BulbOutlined />}
                unCheckedChildren={<BulbOutlined />}
                style={{ marginLeft: "8px", visibility: "collapse" }}
              />
            </div>
            
            <div>
              <Badge 
                count={notificationCount} 
                size="medium"
                offset={[-2, 0]}
              >
                <Button 
                  icon={<BellOutlined style={{ fontSize: '20px', padding: '10px' }} />} 
                  type="text"
                  size="large"
                  onClick={() => {
                    navigate('/notifications');
                  }}
                />
              </Badge>
            </div>

            <div>
              <Dropdown.Button
                menu={menuProps}
                placement="bottom"
                icon={<UserOutlined />}
              >
                {localStorage.getItem("userEmail")}
              </Dropdown.Button>
            </div>
          </Flex>
        </Header>
        <Content
          style={{
            margin: "0 16px",
            borderRadius: "10px",
            padding: "0 10px",
            background: "#fff",
          }}
        >
          <Routes>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/badge-editor" element={<BadgeEditor />} />
            <Route path="/landing-editor" element={<LandingEditor />} />
            <Route path="/landing-editor-desktop" element={<LandingEditorDesktop />} />
            <Route path="/fan-template" element={<FanTemplate />} />
            <Route path="/category-management" element={<CategoryManagement />} />
            <Route path="/info-message-management" element={<InfoMessage />} />
            <Route path="/user-management" element={<UserManagement />} />
            <Route path="/checklist-management" element={<ChecklistPage />} />
            <Route path="/badge-preview/:id" element={<BadgePreview />} />
            <Route path="/badge-edit/:id" element={<BadgeEdit />} />
            <Route path="/notifications" element={<NotificationsPage updateNotificationCount={updateNotificationCount} />} />
            <Route path="/key-management" element={<KeyManagement />} />
          </Routes>
        </Content>
        <Footer
          style={{
            textAlign: "center",
            marginTop: "10px",
          }}
        >
          Orlando Magic Badge App ©{new Date().getFullYear()} Powered by
          BlueCloud
        </Footer>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
